/**
 * resolveCheckoutUrl
 * ----------------------------------------------------------------------------
 * Helper único de resolução do link de pagamento exibido ao pagador final.
 *
 * O sistema mantém SEMPRE dois links para a mesma cobrança:
 *   1) "transparente" — sessão do checkout próprio do sistema (/c/:slug)
 *   2) "provider"    — link nativo do gateway (Asaas invoiceUrl/bankSlipUrl)
 *
 * Qual dos dois é exibido por padrão é definido por
 * `financeiro_conta_corrente.checkout_mode` ('transparent' | 'provider').
 *
 * Em caso de falha do preferido (ex.: incidente de segurança no checkout
 * transparente OU provider sem invoiceUrl ainda sincronizado), o helper faz
 * fallback automático para o outro — garantindo que sempre haja um link
 * clicável enquanto qualquer um dos dois existir.
 *
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial (Fase de contingência).
 */

/** Modo configurado na conta corrente. */
export type CheckoutMode = 'transparent' | 'provider';

/** Origem efetiva do link resolvido. */
export type CheckoutOrigin = 'transparent' | 'provider';

export interface ResolveCheckoutInput {
  /** Modo preferido configurado na conta corrente. Default: 'transparent'. */
  checkout_mode?: CheckoutMode | null;
  /** Slug ativo da sessão de checkout transparente (cache em financeiro_cobrancas). */
  checkout_session_slug?: string | null;
  /** invoiceUrl do provider (preferido entre os links nativos). */
  provider_invoice_url?: string | null;
  /** payment link / id nativo do provider (fallback de fallback). */
  provider_payment_link?: string | null;
}

export interface ResolvedCheckout {
  /** URL absoluta pronta para abrir/copiar. Null se nenhum link disponível. */
  url: string | null;
  /** Qual origem foi efetivamente usada. Null quando `url` é null. */
  origin: CheckoutOrigin | null;
  /** True quando o preferido não estava disponível e usamos contingência. */
  usedFallback: boolean;
  /** Quando true, o sistema precisa (re)gerar o link preferido. */
  needsGeneration: boolean;
}

/**
 * Monta a URL do checkout transparente a partir do slug.
 * Mantemos `window.location.origin` para o link funcionar em qualquer
 * ambiente (dev, preview, produção, white-label).
 */
function buildTransparentUrl(slug: string): string {
  const base =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  return `${base}/c/${slug}`;
}

/**
 * Resolve qual URL exibir ao pagador, aplicando o modo configurado e
 * fallback automático caso o preferido não esteja disponível.
 */
export function resolveCheckoutUrl(input: ResolveCheckoutInput): ResolvedCheckout {
  const mode: CheckoutMode =
    input.checkout_mode === 'provider' ? 'provider' : 'transparent';

  const transparentUrl = input.checkout_session_slug
    ? buildTransparentUrl(input.checkout_session_slug)
    : null;

  const providerUrl =
    input.provider_invoice_url || input.provider_payment_link || null;

  if (mode === 'transparent') {
    if (transparentUrl) {
      return { url: transparentUrl, origin: 'transparent', usedFallback: false, needsGeneration: false };
    }
    if (providerUrl) {
      return { url: providerUrl, origin: 'provider', usedFallback: true, needsGeneration: true };
    }
    return { url: null, origin: null, usedFallback: false, needsGeneration: true };
  }

  // mode === 'provider'
  if (providerUrl) {
    return { url: providerUrl, origin: 'provider', usedFallback: false, needsGeneration: false };
  }
  if (transparentUrl) {
    return { url: transparentUrl, origin: 'transparent', usedFallback: true, needsGeneration: false };
  }
  return { url: null, origin: null, usedFallback: false, needsGeneration: true };
}

/**
 * Atalho que retorna apenas a URL (ou null), útil em handlers simples.
 */
export function pickCheckoutUrl(input: ResolveCheckoutInput): string | null {
  return resolveCheckoutUrl(input).url;
}
